import React, { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green, blue, amber, red, grey } from "@material-ui/core/colors";
import {
	Button,
	IconButton,
	Card,
	CardContent,
	CardActions,
	Grid,
	Avatar,
	Chip,
	Typography,
	Tooltip,
	LinearProgress,
	CircularProgress,
	Box,
} from "@material-ui/core";
import {
	Add,
	Edit,
	CheckCircle,
	Cancel,
	Star,
	StarBorder,
	CropFree,
	DeleteOutline,
	Replay,
	HourglassEmpty,
	Warning as WarningIcon,
	WhatsApp as WhatsAppIcon,
	PowerSettingsNew,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	scroller: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	subtitle: {
		marginBottom: theme.spacing(2),
		color: theme.palette.text.secondary,
	},
	grid: {
		marginTop: theme.spacing(1),
		paddingBottom: theme.spacing(3),
	},
	card: {
		position: "relative",
		overflow: "hidden",
		minHeight: 200,
		display: "flex",
		flexDirection: "column",
		transition: "border-color 0.2s, box-shadow 0.2s",
		borderWidth: 2,
		borderStyle: "solid",
	},
	cardBorderConnected: {
		borderColor: green[500],
	},
	cardBorderConnecting: {
		borderColor: blue[400],
	},
	cardBorderQr: {
		borderColor: amber[600],
	},
	cardBorderDisconnected: {
		borderColor: red[400],
	},
	healthBox: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(1, 1.25),
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.action.hover,
		display: "flex",
		flexDirection: "column",
		gap: 2
	},
	healthReason: { color: theme.palette.text.primary, lineHeight: 1.4 },
	healthAction: { color: theme.palette.text.secondary, lineHeight: 1.4 },
	cardBorderNeutral: {
		borderColor: theme.palette.divider,
	},
	watermark: {
		position: "absolute",
		right: -20,
		top: "50%",
		transform: "translateY(-50%)",
		fontSize: 180,
		opacity: 0.06,
		pointerEvents: "none",
		color: green[500],
	},
	cardHeader: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5),
	},
	avatar: {
		backgroundColor: green[500],
		color: "#fff",
		width: 44,
		height: 44,
	},
	titleBlock: {
		flex: 1,
		minWidth: 0,
	},
	name: {
		fontWeight: 600,
		fontSize: "1rem",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	provider: {
		fontSize: "0.78rem",
		color: theme.palette.text.secondary,
	},
	chips: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(0.75),
		marginBottom: theme.spacing(1.5),
	},
	chipConnected: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(76,175,80,0.15)" : green[50],
		color: green[700],
		fontWeight: 600,
	},
	chipConnecting: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(33,150,243,0.15)" : blue[50],
		color: blue[700],
		fontWeight: 600,
	},
	chipQr: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(255,193,7,0.15)" : amber[50],
		color: amber[800],
		fontWeight: 600,
	},
	chipDisconnected: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(244,67,54,0.15)" : red[50],
		color: red[700],
		fontWeight: 600,
	},
	chipDefault: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(255,193,7,0.15)" : amber[50],
		color: amber[800],
	},
	progressWrap: {
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(1.5),
	},
	progressMeta: {
		display: "flex",
		justifyContent: "space-between",
		marginTop: theme.spacing(0.5),
		fontSize: "0.72rem",
		color: theme.palette.text.secondary,
	},
	actions: {
		marginTop: "auto",
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		paddingTop: 0,
	},
	actionButton: {
		textTransform: "none",
		fontSize: "0.78rem",
	},
	footerMeta: {
		fontSize: "0.7rem",
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.75),
	},
	centered: { textAlign: "center", padding: theme.spacing(6) },
}));

// Calcula segundos restantes ate o proximo retry agendado. Atualiza a cada
// segundo no client; nao depende do socket emitir periodicamente.
const RetryCountdown = ({ runtime }) => {
	const classes = useStyles();
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(id);
	}, []);

	if (!runtime?.nextRetryAt) {
		// Em initializing sem nextRetryAt: barra indeterminada
		return (
			<div className={classes.progressWrap}>
				<LinearProgress />
				<div className={classes.progressMeta}>
					<span>
						{runtime?.attempts > 0
							? `Tentativa ${runtime.attempts}${
									runtime.maxAttempts ? `/${runtime.maxAttempts}` : ""
							  }`
							: "Iniciando..."}
					</span>
				</div>
			</div>
		);
	}

	const target = new Date(runtime.nextRetryAt).getTime();
	const start = runtime.lastAttemptAt
		? new Date(runtime.lastAttemptAt).getTime()
		: target - 5000;
	const totalMs = Math.max(target - start, 1);
	const elapsedMs = Math.max(now - start, 0);
	const remainingMs = Math.max(target - now, 0);
	const pct = Math.min((elapsedMs / totalMs) * 100, 100);
	const seconds = Math.ceil(remainingMs / 1000);

	return (
		<div className={classes.progressWrap}>
			<LinearProgress variant="determinate" value={pct} />
			<div className={classes.progressMeta}>
				<span>
					Tentativa {runtime.attempts}
					{runtime.maxAttempts ? `/${runtime.maxAttempts}` : ""}
				</span>
				<span>
					{remainingMs > 0
						? `Próxima em ${seconds}s`
						: "Tentando agora..."}
				</span>
			</div>
		</div>
	);
};

const ChannelCard = ({
	whatsApp,
	onEdit,
	onQr,
	onRestart,
	onDisconnect,
	onDelete,
	onToggleDefault,
}) => {
	const classes = useStyles();

	const runtime = whatsApp.runtime;

	// `health` vem do backend cruzando o status do banco com a existencia de uma
	// pagina Chromium viva. A coluna do banco sozinha ja mostrou "Conectado" em
	// verde com todo envio falhando — por isso ela deixou de mandar aqui.
	const health = whatsApp.health?.health || "unknown";

	const borderClass =
		health === "connected"
			? classes.cardBorderConnected
			: health === "connecting"
			? classes.cardBorderConnecting
			: health === "needs_qr" || health === "degraded"
			? classes.cardBorderQr
			: health === "disconnected"
			? classes.cardBorderDisconnected
			: classes.cardBorderNeutral;

	const renderStatusChip = () => {
		if (health === "connected") {
			return (
				<Chip
					size="small"
					icon={<CheckCircle style={{ color: green[600] }} />}
					label={i18n.t("connections.status.connected")}
					className={classes.chipConnected}
				/>
			);
		}
		// Estado que nao existia na tela: pareada, porem sem sessao viva neste
		// servidor. Nao e "conectado" (o envio falha) nem "desconectado" (o
		// celular continua pareado) — e mostrar qualquer um dos dois enganava.
		if (health === "degraded") {
			return (
				<Chip
					size="small"
					icon={<WarningIcon style={{ color: amber[800] }} />}
					label={i18n.t("connections.status.degraded")}
					className={classes.chipQr}
				/>
			);
		}
		if (health === "connecting") {
			return (
				<Chip
					size="small"
					icon={<CircularProgress size={12} style={{ color: blue[600] }} />}
					label={i18n.t("connections.status.connecting")}
					className={classes.chipConnecting}
				/>
			);
		}
		if (health === "needs_qr") {
			return (
				<Chip
					size="small"
					icon={<CropFree style={{ color: amber[700] }} />}
					label={i18n.t("connections.status.qr")}
					className={classes.chipQr}
				/>
			);
		}
		if (health === "disconnected") {
			return (
				<Chip
					size="small"
					icon={<Cancel style={{ color: red[600] }} />}
					label={i18n.t("connections.status.disconnected")}
					className={classes.chipDisconnected}
				/>
			);
		}
		return (
			<Chip
				size="small"
				icon={<HourglassEmpty style={{ color: grey[600] }} />}
				label={i18n.t("connections.status.unknown")}
			/>
		);
	};

	// Motivo e proximo passo vem prontos do backend, para a regra nao existir
	// em dois lugares.
	const renderHealthReason = () => {
		const info = whatsApp.health;
		if (!info || health === "connected") return null;
		return (
			<div className={classes.healthBox}>
				<Typography variant="caption" className={classes.healthReason}>
					{info.reason}
				</Typography>
				{info.suggestedAction ? (
					<Typography variant="caption" className={classes.healthAction}>
						{info.suggestedAction}
					</Typography>
				) : null}
			</div>
		);
	};

	return (
		<Card variant="outlined" className={`${classes.card} ${borderClass}`}>
			<WhatsAppIcon className={classes.watermark} />
			<CardContent style={{ paddingBottom: 8 }}>
				<div className={classes.cardHeader}>
					<Avatar className={classes.avatar}>
						<WhatsAppIcon />
					</Avatar>
					<div className={classes.titleBlock}>
						<Typography className={classes.name}>
							{whatsApp.name}
						</Typography>
						<Typography className={classes.provider}>
							{i18n.t("connections.providerLabel")}
						</Typography>
					</div>
				</div>

				<div className={classes.chips}>
					{renderStatusChip()}
					{whatsApp.isDefault && (
						<Chip
							size="small"
							icon={<Star style={{ color: amber[700] }} />}
							label={i18n.t("connections.status.default")}
							className={classes.chipDefault}
						/>
					)}
				</div>

				{health === "connecting" && <RetryCountdown runtime={runtime} />}

				{renderHealthReason()}

				{whatsApp.updatedAt && (
					<Typography className={classes.footerMeta}>
						{i18n.t("connections.lastUpdate")}:{" "}
						{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
					</Typography>
				)}
			</CardContent>

			<CardActions className={classes.actions}>
				{health === "needs_qr" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						className={classes.actionButton}
						startIcon={<CropFree />}
						onClick={() => onQr(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}

				<Button
					size="small"
					variant="outlined"
					className={classes.actionButton}
					startIcon={<Edit />}
					onClick={() => onEdit(whatsApp)}
				>
					{i18n.t("connections.buttons.configure")}
				</Button>

				<Tooltip title={i18n.t("connections.buttons.restart")}>
					<IconButton size="small" onClick={() => onRestart(whatsApp.id)}>
						<Replay fontSize="small" />
					</IconButton>
				</Tooltip>

				{health === "connected" && (
					<Tooltip title={i18n.t("connections.buttons.disconnect")}>
						<IconButton
							size="small"
							onClick={() => onDisconnect(whatsApp.id)}
						>
							<PowerSettingsNew fontSize="small" />
						</IconButton>
					</Tooltip>
				)}

				<Tooltip
					title={
						whatsApp.isDefault
							? i18n.t("connections.buttons.isDefault")
							: i18n.t("connections.buttons.setDefault")
					}
				>
					<IconButton size="small" onClick={() => onToggleDefault(whatsApp)}>
						{whatsApp.isDefault ? (
							<Star fontSize="small" style={{ color: amber[700] }} />
						) : (
							<StarBorder fontSize="small" />
						)}
					</IconButton>
				</Tooltip>

				<Box flexGrow={1} />

				<Tooltip title={i18n.t("connections.buttons.delete")}>
					<IconButton size="small" onClick={() => onDelete(whatsApp.id)}>
						<DeleteOutline fontSize="small" style={{ color: red[400] }} />
					</IconButton>
				</Tooltip>
			</CardActions>
		</Card>
	);
};

const Connections = () => {
	const classes = useStyles();

	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleRestartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}/restart`);
			toast.info(i18n.t("connections.toasts.restarting"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const handleToggleDefault = async whatsApp => {
		if (whatsApp.isDefault) return;
		try {
			await api.put(`/whatsapp/${whatsApp.id}`, {
				name: whatsApp.name,
				isDefault: true,
				greetingMessage: whatsApp.greetingMessage,
				farewellMessage: whatsApp.farewellMessage,
				queueIds: whatsApp.queues?.map(q => q.id) || [],
			});
			toast.success(i18n.t("connections.toasts.defaultSet"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<PageHeader
				title={i18n.t("connections.title")}
				count={whatsApps?.length}
				subtitle={i18n.t("connections.subtitle")}
				actions={
					<Button
						variant="contained"
						color="primary"
						startIcon={<Add />}
						onClick={handleOpenWhatsAppModal}
					>
						{i18n.t("connections.buttons.add")}
					</Button>
				}
			/>

			<div className={classes.scroller}>
				<Grid container spacing={3} className={classes.grid}>
					{loading ? (
						<Grid item xs={12} className={classes.centered}>
							<CircularProgress />
						</Grid>
					) : whatsApps?.length > 0 ? (
						whatsApps.map(whatsApp => (
							<Grid key={whatsApp.id} item xs={12} sm={6} md={4}>
								<ChannelCard
									whatsApp={whatsApp}
									onEdit={handleEditWhatsApp}
									onQr={handleOpenQrModal}
									onRestart={handleRestartWhatsAppSession}
									onDisconnect={id =>
										handleOpenConfirmationModal("disconnect", id)
									}
									onDelete={id => handleOpenConfirmationModal("delete", id)}
									onToggleDefault={handleToggleDefault}
								/>
							</Grid>
						))
					) : (
						<Grid item xs={12}>
							<EmptyState
								icon={<WhatsAppIcon />}
								title={i18n.t("connections.empty")}
								description="Sem um número conectado o sistema não recebe nem envia mensagem. Adicione uma conexão e leia o QR Code no WhatsApp do aparelho."
								action={
									<Button
										variant="contained"
										color="primary"
										startIcon={<Add />}
										onClick={handleOpenWhatsAppModal}
									>
										{i18n.t("connections.buttons.add")}
									</Button>
								}
							/>
						</Grid>
					)}
				</Grid>
			</div>
		</MainContainer>
	);
};

export default Connections;
